import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Geolocation } from "@capacitor/geolocation";
import { NativeSettings, AndroidSettings, IOSSettings } from "capacitor-native-settings";

import { App } from "@capacitor/app";
import * as LiveUpdates from "@capacitor/live-updates";

export function initializeCapacitorApp() {
  // This will ensure that the StatusBar does not overlay the web view and has a certain style and background color.
  // This is useful for Capacitor apps running on iOS and Android.
  StatusBar.setOverlaysWebView({ overlay: false });
  StatusBar.setStyle({ style: Style.Default });
  StatusBar.setBackgroundColor({ color: "#ffffff" });
  // Initialize Live Updates
  checkForUpdates();
}

// Ensures location permission is granted. Requests if possible.
// Opens settings if permanently denied.
export async function ensureLocationPermission(): Promise<boolean> {
  let perm = await Geolocation.checkPermissions();
  if (perm.location === "granted") {
    return true;
  }
  // Try requesting permission if it's still promptable
  if (perm.location === "prompt" || perm.location === "prompt-with-rationale") {
    const newPerm = await Geolocation.requestPermissions();
    return newPerm.location === "granted";
  }
  // If denied, try asking again (Android allows re-prompt unless "Don't ask again")
  if (perm.location === "denied") {
    const newPerm = await Geolocation.requestPermissions();
    if (newPerm.location === "granted") return true;

    // Still denied: direct to system settings
    await openAppSettings();
    return false;
  }
  // Fallback
  return false;
}

// Opens the app settings page in the native settings app.
async function openAppSettings() {
  if (Capacitor.getPlatform() === "ios") {
    await NativeSettings.openIOS({ option: IOSSettings.App });
  } else {
    await NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails });
  }
}

// Function to check for Live Updates on app resume
async function checkForUpdates() {
  // --- Setup event listener for app resume --- //
  // Register event to fire each time user resumes the app
  App.addListener("resume", async () => {
    console.info("App resumed - checking for Live Updates", JSON.stringify(localStorage));
    // --- Live update logic --- //
    if (localStorage.shouldReloadApp === "true") {
      console.info("Reloading app to apply Live Updates");
      await LiveUpdates.reload();
    } else {
      const result = await LiveUpdates.sync();
      localStorage.shouldReloadApp = result.activeApplicationPathChanged;
    }
  });

  // --- Initial check when app starts --- //
  // Location permission is required for some features and it's good to check it on app start. 
  // If not granted, we can request it or direct user to settings.
  const perm = await Geolocation.checkPermissions();
  if (perm.location === "denied") {
    // Handle denied permission case
    console.warn("Location permission is denied. Please enable it in settings.");
  }
  // Start up check for Live Updates to ensure we have the latest version of the app
  const info = await LiveUpdates.getConfig();
  console.info("Current Live Updates config:", JSON.stringify(info, null, 2));
  const result = await LiveUpdates.sync();
  console.info("Live Updates sync result:", JSON.stringify(result, null, 2));
  localStorage.shouldReloadApp = result.activeApplicationPathChanged;
}
